/* Cellform — global.js. Vanilla custom elements; no dependencies. */
(() => {
  const theme = window.theme || {};

  /* ---------- Utilities ---------- */
  const focusableSel = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  const focusable = (el) => [...el.querySelectorAll(focusableSel)].filter((n) => n.offsetParent !== null || n === document.activeElement);
  let trapHandler = null;
  function trapFocus(container, first) {
    removeTrapFocus();
    trapHandler = (e) => {
      if (e.key !== 'Tab') return;
      const els = focusable(container);
      if (!els.length) return;
      const start = els[0], end = els[els.length - 1];
      if (e.shiftKey && document.activeElement === start) { e.preventDefault(); end.focus(); }
      else if (!e.shiftKey && document.activeElement === end) { e.preventDefault(); start.focus(); }
    };
    document.addEventListener('keydown', trapHandler);
    (first || focusable(container)[0] || container).focus({ preventScroll: true });
  }
  function removeTrapFocus(returnTo) {
    if (trapHandler) document.removeEventListener('keydown', trapHandler);
    trapHandler = null;
    if (returnTo) returnTo.focus({ preventScroll: true });
  }
  const debounce = (fn, wait = 300) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };
  const fetchJSON = (url, body) => fetch(url, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
  }).then(async (r) => { const j = await r.json(); if (!r.ok || j.status) throw j; return j; });
  const announce = (msg) => { const r = document.getElementById('LiveRegion'); if (r) { r.textContent = ''; setTimeout(() => { r.textContent = msg; }, 50); } };
  const store = {
    get(k) { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* storage unavailable */ } }
  };
  const formatMoney = (cents) => {
    const fmt = theme.moneyFormat || '£{{amount}}';
    const value = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => (k === 'amount_no_decimals' ? Math.round(cents / 100) : value));
  };
  const publish = (name, data) => { try { window.Shopify && Shopify.analytics && Shopify.analytics.publish(name, data); } catch (e) { /* noop */ } };
  window.Cellform = { trapFocus, removeTrapFocus, debounce, fetchJSON, announce, store, formatMoney, publish };

  /* ---------- Sticky header: visible on scroll-up only ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.last = window.scrollY;
      this.onScroll = () => {
        const y = window.scrollY;
        const open = this.querySelector('details[open]');
        if (y > this.offsetHeight && y > this.last && !open) this.classList.add('is-hidden');
        else this.classList.remove('is-hidden');
        this.last = y;
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('focusin', () => this.classList.remove('is-hidden'));
      // Mega menus: one open at a time, close on Escape / outside click / focus leaving.
      this.querySelectorAll('.mega-details').forEach((d) => {
        d.addEventListener('toggle', () => { if (d.open) this.querySelectorAll('.mega-details[open]').forEach((o) => { if (o !== d) o.open = false; }); });
        d.addEventListener('keydown', (e) => { if (e.key === 'Escape' && d.open) { d.open = false; d.querySelector('summary').focus(); } });
        d.addEventListener('focusout', (e) => { if (!d.contains(e.relatedTarget) && e.relatedTarget) d.open = false; });
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.querySelectorAll('.mega-details[open]').forEach((d) => { d.open = false; }); });
      const searchToggle = this.querySelector('[data-toggle-search]');
      const mobileSearch = this.querySelector('#MobileSearch');
      if (searchToggle && mobileSearch) {
        searchToggle.addEventListener('click', () => {
          const show = mobileSearch.hidden;
          mobileSearch.hidden = !show;
          searchToggle.setAttribute('aria-expanded', String(show));
          if (show) mobileSearch.querySelector('input[type="search"]').focus();
        });
      }
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Drawers ---------- */
  class Drawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const panel = this.querySelector('.drawer__panel');
      setTimeout(() => trapFocus(panel), 50);
    }
    close() {
      this.classList.remove('is-open');
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      removeTrapFocus(this.opener);
    }
  }
  window.Cellform.Drawer = Drawer;
  customElements.define('menu-drawer', class extends Drawer {});
  customElements.define('quick-view-drawer', class extends Drawer {});

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (drawer && typeof drawer.open === 'function') { e.preventDefault(); drawer.open(opener); }
  });

  /* ---------- Announcement bar: rotate, pause on hover/focus, dismiss ---------- */
  customElements.define('announcement-bar', class extends HTMLElement {
    connectedCallback() {
      try { if (this.hasAttribute('data-dismissible') && sessionStorage.getItem('cf-announcement-dismissed')) { this.hidden = true; return; } } catch (e) { /* noop */ }
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.index = 0;
      const delay = (parseInt(this.dataset.autoplay, 10) || 5) * 1000;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.slides.length > 1 && !reduce) {
        this.timer = setInterval(() => { if (!this.paused) this.next(); }, delay);
        ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => { this.paused = true; }));
        ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => { this.paused = false; }));
      }
      const dismiss = this.querySelector('[data-dismiss]');
      if (dismiss) dismiss.addEventListener('click', () => {
        this.hidden = true;
        try { sessionStorage.setItem('cf-announcement-dismissed', '1'); } catch (e) { /* noop */ }
      });
    }
    next() {
      this.slides[this.index].hidden = true;
      this.index = (this.index + 1) % this.slides.length;
      this.slides[this.index].hidden = false;
    }
    disconnectedCallback() { clearInterval(this.timer); }
  });

  /* ---------- Quantity input ---------- */
  customElements.define('quantity-input', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        e.preventDefault();
        const prev = this.input.value;
        btn.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
        if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  });

  /* ---------- Add to cart (quick add, main product form, recommendations) ---------- */
  async function addToCart(formOrItems, button) {
    const sections = ['cart-drawer', 'cart-icon-bubble'];
    let body;
    if (formOrItems instanceof HTMLFormElement) {
      const fd = new FormData(formOrItems);
      const items = [{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }];
      if (fd.get('selling_plan')) items[0].selling_plan = Number(fd.get('selling_plan'));
      for (const [k, v] of fd.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m && v) { items[0].properties = items[0].properties || {}; items[0].properties[m[1]] = v; }
      }
      body = { items };
    } else body = { items: formOrItems };
    body.sections = sections;
    body.sections_url = window.location.pathname;
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-disabled', 'true'); }
    try {
      const res = await fetchJSON(`${theme.routes.cartAdd}.js`, body);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { sections: res.sections, source: 'add', items: res.items } }));
      if (theme.cartType !== 'drawer') { window.location = theme.routes.cart; return res; }
      const drawer = document.getElementById('CartDrawer');
      if (drawer) drawer.open(button);
      announce(theme.strings.added);
      return res;
    } catch (err) {
      const msg = (err && (err.description || err.message)) || theme.strings.cartError;
      document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: msg } }));
      announce(msg);
      throw err;
    } finally {
      if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-disabled'); }
    }
  }
  window.Cellform.addToCart = addToCart;

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form || !theme.routes) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const errorEl = form.querySelector('[data-form-error]');
    if (errorEl) errorEl.hidden = true;
    addToCart(form, btn).catch((err) => {
      if (errorEl) { errorEl.textContent = (err && err.description) || theme.strings.cartError; errorEl.hidden = false; }
    });
  });

  /* ---------- Quick view (multi-variant quick add) ---------- */
  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (!trigger) return;
    e.preventDefault();
    let drawer = document.getElementById('QuickViewDrawer');
    if (!drawer) {
      drawer = document.createElement('quick-view-drawer');
      drawer.id = 'QuickViewDrawer';
      drawer.className = 'drawer';
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      drawer.setAttribute('aria-label', trigger.textContent.trim());
      drawer.innerHTML = '<div class="drawer__overlay" data-drawer-close></div><div class="drawer__panel"></div>';
      document.body.appendChild(drawer);
    }
    trigger.classList.add('is-loading');
    try {
      const url = trigger.dataset.quickView.split('?')[0];
      const html = await fetch(`${url}?section_id=quick-view`).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.quick-view');
      drawer.querySelector('.drawer__panel').replaceChildren(content || doc.body);
      if (!customElements.get('product-info')) await loadScript(drawer.querySelector('[data-product-form-src]')?.dataset.productFormSrc);
      drawer.open(trigger);
    } finally { trigger.classList.remove('is-loading'); }
  });
  function loadScript(src) {
    return new Promise((resolve) => {
      if (!src || document.querySelector(`script[src="${src}"]`)) return resolve();
      const s = document.createElement('script'); s.src = src; s.onload = resolve; s.onerror = resolve; document.head.appendChild(s);
    });
  }

  /* ---------- Product tabs (filter carousel by product type; all visible without JS) ---------- */
  customElements.define('product-tabs', class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.items = [...this.querySelectorAll('[data-product-type]')].map((el) => el.closest('li') || el);
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab)));
      this.addEventListener('keydown', (e) => {
        const i = this.tabs.indexOf(document.activeElement);
        if (i < 0) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); this.tabs[(i + 1) % this.tabs.length].focus(); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.tabs[(i - 1 + this.tabs.length) % this.tabs.length].focus(); }
      });
    }
    select(tab) {
      this.tabs.forEach((t) => { t.setAttribute('aria-selected', String(t === tab)); t.tabIndex = t === tab ? 0 : -1; });
      const type = tab.dataset.type;
      this.items.forEach((li) => {
        const card = li.querySelector('[data-product-type]') || li;
        li.hidden = !!type && card.dataset.productType !== type;
      });
      const slider = this.querySelector('.slider');
      if (slider) slider.scrollTo({ left: 0 });
    }
  });

  /* ---------- Slider prev/next ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-slider-dir]');
    if (!btn) return;
    const slider = document.getElementById(btn.getAttribute('aria-controls'));
    if (slider) slider.scrollBy({ left: (btn.dataset.sliderDir === 'next' ? 1 : -1) * slider.clientWidth * 0.8, behavior: 'smooth' });
  });

  /* ---------- Newsletter modal (desktop only, once per 14 days) ---------- */
  customElements.define('newsletter-modal', class extends HTMLElement {
    connectedCallback() {
      if (!window.matchMedia('(min-width: 990px)').matches) return;
      if (window.Shopify && Shopify.designMode) return;
      const last = store.get('cf-newsletter-seen');
      if (last && Date.now() - last < 14 * 864e5) return;
      if (document.querySelector('.newsletter-form .form-message--success')) return;
      const show = () => {
        if (this.shown) return;
        this.shown = true;
        store.set('cf-newsletter-seen', Date.now());
        this.hidden = false;
        this.opener = document.activeElement;
        trapFocus(this.querySelector('.modal__panel'));
      };
      this.timer = setTimeout(show, (parseInt(this.dataset.delay, 10) || 20) * 1000);
      if (this.dataset.exitIntent === 'true') {
        document.addEventListener('mouseout', (e) => { if (!e.relatedTarget && e.clientY < 10) show(); });
      }
      this.addEventListener('click', (e) => { if (e.target.closest('[data-modal-close]') || e.target.classList.contains('modal')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    close() { this.hidden = true; removeTrapFocus(this.opener); }
  });

  /* ---------- Recently viewed (localStorage) ---------- */
  const RV_KEY = 'cf-recently-viewed';
  const current = document.querySelector('[data-recently-viewed-handle]');
  if (current) {
    const handle = current.dataset.recentlyViewedHandle;
    const list = (store.get(RV_KEY) || []).filter((h) => h !== handle);
    list.unshift(handle);
    store.set(RV_KEY, list.slice(0, 12));
  }
  customElements.define('recently-viewed', class extends HTMLElement {
    async connectedCallback() {
      const exclude = this.dataset.exclude;
      const handles = (store.get(RV_KEY) || []).filter((h) => h !== exclude).slice(0, parseInt(this.dataset.limit, 10) || 4);
      if (!handles.length) return;
      const products = (await Promise.all(handles.map((h) => fetch(`${theme.routes.root.replace(/\/$/, '')}/products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)))).filter(Boolean);
      if (!products.length) return;
      const ul = this.querySelector('ul');
      ul.innerHTML = products.map((p) => {
        const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=400` : '';
        const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
        return `<li><article class="card"><a class="card__media" href="${p.url}" tabindex="-1" aria-hidden="true"><div class="media-well">${img ? `<img src="${img}" alt="" loading="lazy" width="400" height="400">` : ''}</div></a>
          <div class="card__body"><p class="card__vendor">${esc(p.vendor)}</p><h3 class="card__title"><a href="${p.url}">${esc(p.title)}</a></h3>
          <div class="card__footer"><div class="price">${formatMoney(p.price)}</div></div></div></article></li>`;
      }).join('');
      this.hidden = false;
    }
  });

  /* ---------- Lazy section (recommendations etc.) ---------- */
  customElements.define('lazy-section', class extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        try {
          const html = await fetch(this.dataset.url).then((r) => r.text());
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const section = doc.querySelector('.shopify-section');
          if (section && section.children.length) this.replaceChildren(...section.children);
          else { const band = this.closest('.section'); if (band) band.hidden = true; }
        } catch (e) { const band = this.closest('.section'); if (band) band.hidden = true; }
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '300px' });
      io.observe(this);
    }
  });

  /* ---------- Analytics: select_item on product card click ---------- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.product-card a[href]');
    if (!link) return;
    const card = link.closest('.product-card');
    publish('select_item', { product_id: card.dataset.productId, list: card.dataset.list || document.title, position: Number(card.dataset.position || 0) });
  });

  /* ---------- Load more (progressive enhancement over crawlable ?page= links) ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-load-more]');
    if (!btn) return;
    e.preventDefault();
    btn.classList.add('is-loading');
    try {
      const html = await fetch(btn.href).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const grid = document.getElementById(btn.dataset.loadMore);
      const next = doc.getElementById(btn.dataset.loadMore);
      if (grid && next) grid.append(...next.children);
      const nextBtn = doc.querySelector(`[data-load-more="${btn.dataset.loadMore}"]`);
      const pagination = doc.querySelector('.pagination');
      const curPagination = document.querySelector('.pagination');
      if (curPagination && pagination) curPagination.replaceWith(pagination);
      if (nextBtn) { btn.href = nextBtn.href; } else { btn.parentElement.hidden = true; }
    } finally { btn.classList.remove('is-loading'); }
  });

  /* ---------- Keep free-delivery messages in sync after cart changes ---------- */
  document.addEventListener('cart:totals', (e) => {
    const total = e.detail.total;
    const threshold = theme.freeShippingThreshold || 0;
    if (!threshold) return;
    document.querySelectorAll('[data-free-shipping-msg]').forEach((el) => {
      const remaining = threshold - total;
      el.innerHTML = remaining > 0
        ? (el.dataset.remainingTemplate || '').replace('[amount]', formatMoney(remaining))
        : el.dataset.unlockedTemplate || '';
    });
  });
})();
